import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/shared/components/data-table/data-table-column-header';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';

import type { ReportFilterChanges } from '../lib/report-filters';
import type { FinancialTransaction, FinancialTransactionSort } from '../types/report.types';
import { FilterValue } from './filter-value';

/** The columns the API can sort by — the column ids are the API's sort keys. */
export const TRANSACTION_SORTS: readonly FinancialTransactionSort[] = [
  'date',
  'collected',
  'grossProfit',
  'partnerShare',
  'channel',
  'provider',
  'brand',
];

/**
 * Hidden by default; one click away in "Columns". Partner share has its own
 * sub-tab, and is 0 wherever no partner share applies.
 */
export const TRANSACTION_HIDDEN_COLUMNS = {
  discount: false,
  channelFee: false,
  partnerShare: false,
};

/**
 * An amount the order has no pricing snapshot for: shown as a dash, with the
 * reason for screen readers and on hover.
 */
function Unpriced() {
  return (
    <span title="No pricing snapshot">
      <span aria-hidden>—</span>
      <span className="sr-only">No pricing snapshot</span>
    </span>
  );
}

function Money({ value, row }: { value: number | null; row: FinancialTransaction }) {
  if (value === null) return row.isPriced ? <>—</> : <Unpriced />;
  return <>{formatCurrency(value, row.currencyCode)}</>;
}

function moneyColumn(
  id: keyof FinancialTransaction & string,
  title: string,
  value: (row: FinancialTransaction) => number | null,
  sortable: boolean,
): ColumnDef<FinancialTransaction, unknown> {
  return {
    id,
    accessorFn: value,
    header: ({ column }) => <DataTableColumnHeader column={column} title={title} align="end" />,
    enableSorting: sortable,
    meta: { label: title },
    cell: ({ row }) => (
      <div className="text-end tabular-nums">
        <Money value={value(row.original)} row={row.original} />
      </div>
    ),
  };
}

/**
 * Columns of the financial report's transactions: what was bought (brand,
 * denomination), how it was paid (channel), which provider fulfilled it at what
 * price, and the order's money. Brand, denomination, channel and provider
 * narrow the whole report when clicked.
 */
export function createTransactionColumns({
  onFilter,
  canOpenOrder,
  withCost = true,
  zone,
}: {
  /** The report's zone (the market's, or Palestine for all markets). */
  zone: string;
  onFilter?: (changes: ReportFilterChanges) => void;
  /** orders.details.read — otherwise the order id is plain text. */
  canOpenOrder: boolean;
  /** reports.financial.read — otherwise (the Partner) only Total and Partner share of the money. */
  withCost?: boolean;
}): ColumnDef<FinancialTransaction, unknown>[] {
  const apply = (changes: ReportFilterChanges) => (onFilter ? () => onFilter(changes) : undefined);
  const costOnly = (column: ColumnDef<FinancialTransaction, unknown>) => (withCost ? [column] : []);

  return [
    {
      id: 'date',
      accessorKey: 'date',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      enableHiding: false,
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{formatDateTime(row.original.date, zone)}</span>
      ),
    },
    {
      id: 'orderId',
      accessorKey: 'orderId',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Order" />,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) =>
        canOpenOrder ? (
          <Link
            to={`/orders/${row.original.orderId}`}
            className="text-primary hover:text-primary/80 font-mono text-xs font-medium hover:underline"
          >
            #{row.original.orderId}
          </Link>
        ) : (
          <span className="font-mono">#{row.original.orderId}</span>
        ),
    },
    {
      id: 'brand',
      accessorKey: 'brandName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Brand" />,
      meta: { label: 'Brand' },
      cell: ({ row }) => {
        const { brandId, brandName } = row.original;
        if (!brandName) return <span className="text-muted-foreground">Unknown</span>;
        return (
          <FilterValue
            description={`brand ${brandName}`}
            onApply={
              brandId === null
                ? undefined
                : apply({ brandId: String(brandId), denominationId: null })
            }
          >
            {brandName}
          </FilterValue>
        );
      },
    },
    {
      id: 'denomination',
      accessorKey: 'denominationName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Denomination" />,
      enableSorting: false,
      meta: { label: 'Denomination' },
      cell: ({ row }) => {
        const { brandId, denominationId, denominationName, brandName } = row.original;
        if (!denominationName) return <span className="text-muted-foreground">Unknown</span>;
        return (
          <FilterValue
            description={`denomination ${brandName ? `${brandName} ` : ''}${denominationName}`}
            onApply={
              denominationId === null
                ? undefined
                : apply({
                    brandId: brandId === null ? null : String(brandId),
                    denominationId: String(denominationId),
                  })
            }
          >
            {denominationName}
          </FilterValue>
        );
      },
    },
    {
      id: 'channel',
      accessorKey: 'channelCode',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Payment channel" />,
      meta: { label: 'Payment channel' },
      cell: ({ row }) => {
        const { channelCode, channelName } = row.original;
        if (!channelCode) return <span className="text-muted-foreground">Unknown</span>;
        return (
          <div className="min-w-0">
            <FilterValue
              description={`payment channel ${channelName ?? channelCode}`}
              onApply={apply({ channel: channelCode })}
            >
              {channelName ?? channelCode}
            </FilterValue>
            {channelName && (
              <span className="text-muted-foreground block font-mono text-[10px]">
                {channelCode}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'provider',
      accessorKey: 'providerName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
      meta: { label: 'Provider' },
      cell: ({ row }) => {
        const { providerId, providerName, providerPrice, providerCurrency } = row.original;
        if (!providerName) return <span className="text-muted-foreground">Unknown</span>;
        return (
          <div className="min-w-0">
            <FilterValue
              description={`provider ${providerName}`}
              onApply={providerId === null ? undefined : apply({ providerId: String(providerId) })}
            >
              {providerName}
            </FilterValue>
            {providerPrice !== null && (
              <span className="text-muted-foreground block text-[11px] tabular-nums">
                {providerCurrency
                  ? formatCurrency(providerPrice, providerCurrency, { precise: true })
                  : providerPrice}
              </span>
            )}
          </div>
        );
      },
    },
    moneyColumn('collected', withCost ? 'Collected' : 'Total', (row) => row.collected, true),
    ...costOnly(moneyColumn('discount', 'Discount', (row) => row.discount, false)),
    ...costOnly(moneyColumn('channelFee', 'Channel fee', (row) => row.channelFee, false)),
    ...costOnly(moneyColumn('providerCost', 'Provider cost', (row) => row.providerCost, false)),
    ...costOnly(moneyColumn('grossProfit', 'Gross profit', (row) => row.grossProfit, true)),
    moneyColumn('partnerShare', 'Partner share', (row) => row.partnerShare, true),
    ...costOnly({
      id: 'realizedMarginPct',
      accessorKey: 'realizedMarginPct',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Margin %" align="end" />
      ),
      enableSorting: false,
      meta: { label: 'Margin %' },
      cell: ({ row }) => {
        const { realizedMarginPct, isPriced } = row.original;
        return (
          <div className="text-end tabular-nums">
            {realizedMarginPct !== null ? `${realizedMarginPct}%` : isPriced ? '—' : <Unpriced />}
          </div>
        );
      },
    }),
    {
      id: 'billerTrxNo',
      accessorKey: 'billerTrxNo',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Biller trx no" />,
      enableSorting: false,
      meta: { label: 'Biller trx no' },
      cell: ({ row }) => (
        <span className="font-mono text-[11px]">{row.original.billerTrxNo ?? '—'}</span>
      ),
    },
  ];
}
